/**
 * Classifies a mail attachment into the kinds the dashboard renders inline.
 *
 * Attachments are fetched as a Blob and shown from a blob: URL, which lives in
 * this app's own origin. Anything outside the allowlist below is therefore
 * offered as a download only — an HTML or SVG attachment rendered from that URL
 * would run its scripts with the dashboard's session.
 */

/** Text bigger than this is not worth rendering into the dialog. */
export const MAX_TEXT_PREVIEW_BYTES = 256 * 1024;

const NO_PREVIEW = { kind: 'none', mimeType: 'application/octet-stream' };

const IMAGE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/avif',
]);

const TEXT_MIME_TYPES = new Set(['text/plain', 'text/csv']);

/** Types that carry no information — fall back to the file extension for these. */
const GENERIC_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/binary',
]);

const EXTENSION_MIME_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  avif: 'image/avif',
  txt: 'text/plain',
  log: 'text/plain',
  csv: 'text/csv',
};

/** Lower-cases a Content-Type and drops its parameters (`; charset=utf-8`). */
const normalizeMimeType = contentType => {
  if (typeof contentType !== 'string') return '';
  return contentType.split(';')[0].trim().toLowerCase();
};

/** Lower-cased extension of a file name, without the dot. */
const fileExtension = fileName => {
  if (typeof fileName !== 'string') return '';
  const match = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  return match ? match[1].toLowerCase() : '';
};

const previewForMimeType = mimeType => {
  if (mimeType === 'application/pdf') return { kind: 'pdf', mimeType: 'application/pdf' };
  if (IMAGE_MIME_TYPES.has(mimeType)) {
    return { kind: 'image', mimeType: mimeType === 'image/jpg' ? 'image/jpeg' : mimeType };
  }
  if (TEXT_MIME_TYPES.has(mimeType)) return { kind: 'text', mimeType: 'text/plain' };
  return null;
};

/**
 * Decides how an attachment may be shown. The declared Content-Type wins; the
 * file extension is only consulted when the mail carries no usable type, so a
 * `.pdf` name cannot talk us into rendering something declared as HTML.
 */
export const resolveAttachmentPreview = ({ contentType, fileName, size } = {}) => {
  const mimeType = normalizeMimeType(contentType);
  const resolved = GENERIC_MIME_TYPES.has(mimeType)
    ? previewForMimeType(EXTENSION_MIME_TYPES[fileExtension(fileName)] || '')
    : previewForMimeType(mimeType);

  if (!resolved) return NO_PREVIEW;
  if (resolved.kind === 'text' && typeof size === 'number' && size > MAX_TEXT_PREVIEW_BYTES) {
    return NO_PREVIEW;
  }
  return resolved;
};
